import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { MasterServiceItem, QuoteType } from '../types/admin';

export interface MasterServiceItemInput {
  name: string;
  code?: string;
  party: 'Customer' | 'Mover';
  note?: string;
  quoteType?: QuoteType;
}

export function useMasterServices() {
  const [items, setItems] = useState<MasterServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: MasterServiceItem[] }>(
        '/admin/master-services',
      );
      setItems(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const createItem = async (input: MasterServiceItemInput): Promise<MasterServiceItem> => {
    const res = await api.post<{ success: boolean; data: MasterServiceItem }>(
      '/admin/master-services',
      input,
    );
    await fetchItems(); // Refetch so ordering is correct
    return res.data;
  };

  const updateItem = async (
    id: string,
    input: Partial<MasterServiceItemInput>,
  ): Promise<MasterServiceItem> => {
    const res = await api.patch<{ success: boolean; data: MasterServiceItem }>(
      `/admin/master-services/${id}`,
      input,
    );
    setItems(prev => prev.map(item => (item.id === id ? res.data : item)));
    return res.data;
  };

  const deleteItem = async (id: string): Promise<void> => {
    await api.del(`/admin/master-services/${id}`);
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const reorderItems = async (orderedIds: string[]): Promise<void> => {
    await api.post('/admin/master-services/reorder', {
      order: orderedIds.map(id => ({ id })),
    });
    await fetchItems();
  };

  const bulkUpload = async (csv: string): Promise<{ created: number; updated: number }> => {
    const res = await api.post<{
      success: boolean;
      data: { created: number; updated: number };
    }>('/admin/master-services/bulk-upload', { csv });
    await fetchItems();
    return res.data;
  };

  const downloadJson = async (): Promise<void> => {
    const res = await api.get<{ success: boolean; data: Record<string, unknown>[] }>(
      '/admin/master-services/download-json',
    );
    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'master-services.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const bulkUploadJson = async (
    items: Record<string, unknown>[],
  ): Promise<{ created: number; updated: number }> => {
    const res = await api.post<{
      success: boolean;
      data: { created: number; updated: number };
    }>('/admin/master-services/bulk-upload-json', { items });
    await fetchItems();
    return res.data;
  };

  return {
    items,
    loading,
    error,
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
    bulkUpload,
    downloadJson,
    bulkUploadJson,
    refetch: fetchItems,
  };
}
